// Galley zone — restaurants, orders, drivers, routes, customers, analytics
import type {
  GalleyRestaurant, GalleyMenuItem, GalleyDeliveryWindow,
  GalleyOrder, GalleyDriver, GalleyRoute, GalleyRouteStop,
  GalleyCustomer, GalleyAnalyticsOverview, GalleyDeliveryMetrics,
  GalleyDriverAssignment, GalleyDriverEarnings, GalleyOrderPlacement,
} from './types'
import type { RequestHelper } from './client'

export function galleyMethods(r: RequestHelper) {
  return {
    restaurants: {
      list: (projectId: string) =>
        r.get<{ restaurants: GalleyRestaurant[] }>(`/hub/projects/${projectId}/galley/restaurants`),
      get: (projectId: string, id: string) =>
        r.get<{ restaurant: GalleyRestaurant }>(`/hub/projects/${projectId}/galley/restaurants/${id}`),
      create: (projectId: string, data: Partial<GalleyRestaurant>) =>
        r.post<{ restaurant: GalleyRestaurant }>(`/hub/projects/${projectId}/galley/restaurants`, data),
      update: (projectId: string, id: string, data: Partial<GalleyRestaurant>) =>
        r.put<{ restaurant: GalleyRestaurant }>(`/hub/projects/${projectId}/galley/restaurants/${id}`, data),
      delete: (projectId: string, id: string) =>
        r.del<void>(`/hub/projects/${projectId}/galley/restaurants/${id}`),
      menu: {
        list: (projectId: string, restaurantId: string) =>
          r.get<{ items: GalleyMenuItem[] }>(`/hub/projects/${projectId}/galley/restaurants/${restaurantId}/menu`),
        create: (projectId: string, restaurantId: string, data: Partial<GalleyMenuItem> & { category_name?: string }) =>
          r.post<{ item: GalleyMenuItem }>(`/hub/projects/${projectId}/galley/restaurants/${restaurantId}/menu`, data),
      },
      menuItem: {
        update: (projectId: string, itemId: string, data: Partial<GalleyMenuItem>) =>
          r.put<{ item: GalleyMenuItem }>(`/hub/projects/${projectId}/galley/menu-items/${itemId}`, data),
        delete: (projectId: string, itemId: string) =>
          r.del<void>(`/hub/projects/${projectId}/galley/menu-items/${itemId}`),
      },
      orders: (projectId: string, restaurantId: string) =>
        r.get<{ orders: GalleyOrder[] }>(`/hub/projects/${projectId}/galley/restaurants/${restaurantId}/orders`),
      confirmOrder: (projectId: string, restaurantId: string, orderId: string) =>
        r.put<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/restaurants/${restaurantId}/orders/${orderId}/confirm`),
    },

    orders: {
      list: (projectId: string, params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ orders: GalleyOrder[] }>(`/hub/projects/${projectId}/galley/orders${query}`)
      },
      get: (projectId: string, id: string) =>
        r.get<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/orders/${id}`),
      place: (projectId: string, data: GalleyOrderPlacement) =>
        r.post<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/orders`, data),
      updateStatus: (projectId: string, id: string, status: string) =>
        r.put<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/orders/${id}/status`, { status }),
      cancel: (projectId: string, id: string) =>
        r.post<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/orders/${id}/cancel`),
      tracking: (projectId: string, id: string) =>
        r.get<{ order: GalleyOrder }>(`/hub/projects/${projectId}/galley/orders/${id}/tracking`),
    },

    deliveryWindows: {
      list: (projectId: string) =>
        r.get<{ windows: GalleyDeliveryWindow[] }>(`/hub/projects/${projectId}/galley/windows`),
      get: (projectId: string, id: string) =>
        r.get<{ window: GalleyDeliveryWindow }>(`/hub/projects/${projectId}/galley/windows/${id}`),
      create: (projectId: string, data: Partial<GalleyDeliveryWindow>) =>
        r.post<{ window: GalleyDeliveryWindow }>(`/hub/projects/${projectId}/galley/windows`, data),
      update: (projectId: string, id: string, data: Partial<GalleyDeliveryWindow>) =>
        r.put<{ window: GalleyDeliveryWindow }>(`/hub/projects/${projectId}/galley/windows/${id}`, data),
      delete: (projectId: string, id: string) =>
        r.del<void>(`/hub/projects/${projectId}/galley/windows/${id}`),
    },

    drivers: {
      list: (projectId: string) =>
        r.get<{ drivers: GalleyDriver[] }>(`/hub/projects/${projectId}/galley/drivers`),
      get: (projectId: string, id: string) =>
        r.get<{ driver: GalleyDriver }>(`/hub/projects/${projectId}/galley/drivers/${id}`),
      create: (projectId: string, data: Partial<GalleyDriver>) =>
        r.post<{ driver: GalleyDriver }>(`/hub/projects/${projectId}/galley/drivers`, data),
      update: (projectId: string, id: string, data: Partial<GalleyDriver>) =>
        r.put<{ driver: GalleyDriver }>(`/hub/projects/${projectId}/galley/drivers/${id}`, data),
      setAvailability: (projectId: string, id: string, data: { is_available: boolean; delivery_window_id?: string }) =>
        r.put<{ driver: GalleyDriver }>(`/hub/projects/${projectId}/galley/drivers/${id}/availability`, data),
      updateLocation: (projectId: string, id: string, lat: number, lng: number) =>
        r.put<{ success: boolean }>(`/hub/projects/${projectId}/galley/drivers/${id}/location`, { lat, lng }),
      assignments: (projectId: string, id: string) =>
        r.get<{ assignments: GalleyDriverAssignment[] }>(`/hub/projects/${projectId}/galley/drivers/${id}/assignments`),
      earnings: (projectId: string, id: string) =>
        r.get<{ earnings: GalleyDriverEarnings }>(`/hub/projects/${projectId}/galley/drivers/${id}/earnings`),
    },

    galleyRoutes: {
      list: (projectId: string, windowId?: string) => {
        const query = windowId ? `?window_id=${windowId}` : ''
        return r.get<{ routes: GalleyRoute[] }>(`/hub/projects/${projectId}/galley/routes${query}`)
      },
      get: (projectId: string, id: string) =>
        r.get<{ route: GalleyRoute; stops: GalleyRouteStop[] }>(`/hub/projects/${projectId}/galley/routes/${id}`),
      optimize: (projectId: string, deliveryWindowId: string) =>
        r.post<{ routes: GalleyRoute[]; orderCount: number; driverCount: number }>(
          `/hub/projects/${projectId}/galley/routes/optimize`,
          { delivery_window_id: deliveryWindowId }
        ),
      start: (projectId: string, id: string) =>
        r.put<{ route: GalleyRoute }>(`/hub/projects/${projectId}/galley/routes/${id}/start`),
      arriveAtStop: (projectId: string, routeId: string, stopId: string) =>
        r.put<{ stop: GalleyRouteStop }>(`/hub/projects/${projectId}/galley/routes/${routeId}/stops/${stopId}/arrive`),
      complete: (projectId: string, id: string) =>
        r.put<{ success: boolean }>(`/hub/projects/${projectId}/galley/routes/${id}/complete`),
    },

    galleyCustomers: {
      list: (projectId: string, params?: { limit?: number; offset?: number }) => {
        const query = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ''
        return r.get<{ customers: GalleyCustomer[] }>(`/hub/projects/${projectId}/galley/customers${query}`)
      },
      get: (projectId: string, id: string) =>
        r.get<{ customer: GalleyCustomer }>(`/hub/projects/${projectId}/galley/customers/${id}`),
      create: (projectId: string, data: Partial<GalleyCustomer>) =>
        r.post<{ customer: GalleyCustomer }>(`/hub/projects/${projectId}/galley/customers`, data),
      update: (projectId: string, id: string, data: Partial<GalleyCustomer>) =>
        r.put<{ customer: GalleyCustomer }>(`/hub/projects/${projectId}/galley/customers/${id}`, data),
    },

    galleyAnalytics: {
      overview: (projectId: string) =>
        r.get<{ overview: GalleyAnalyticsOverview }>(`/hub/projects/${projectId}/galley/analytics/overview`),
      deliveryPerformance: (projectId: string) =>
        r.get<{ metrics: GalleyDeliveryMetrics[] }>(`/hub/projects/${projectId}/galley/analytics/delivery-performance`),
      driverEfficiency: (projectId: string) =>
        r.get<{ drivers: GalleyDriver[] }>(`/hub/projects/${projectId}/galley/analytics/driver-efficiency`),
      restaurantPerformance: (projectId: string) =>
        r.get<{ restaurants: GalleyRestaurant[] }>(`/hub/projects/${projectId}/galley/analytics/restaurant-performance`),
    },
  }
}
